import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15 } from "styled-components";
import React from "react";
import { Wrap } from "~/features/ui/wrap";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { parseTextWithHighlights } from "~/features/ui/headline";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { GraphicStatsStat } from "~/features/blocks/inventory/graphic-stats/stat";
import { GraphicStatsGraphicView, graphicStatsGraphicFragment } from "~/features/blocks/inventory/graphic-stats/graphic";
import { GraphicStatsGraphicBar } from "~/features/blocks/inventory/graphic-stats/graphic/graphic-bar";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type GraphicStatsBlockViewProps<TStat, TBar> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  callToActionText?: string | null;
  caption?: string | null;
  getBarKey?: (bar: TBar, index: number) => React.Key;
  getStatKey?: (stat: TStat, index: number) => React.Key;
  graphic?: readonly TBar[] | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mode?: string | null;
  renderBar: (bar: TBar, index: number, reveal: boolean) => React.ReactNode;
  renderStat: (stat: TStat, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  stats?: readonly TStat[] | null;
  theme?: string | null;
};
export function GraphicStatsBlockView<TStat, TBar>({
  anchor,
  callToActionText,
  caption,
  getBarKey,
  getStatKey,
  graphic = [],
  hasTopDivider = true,
  headline,
  link: linkValue,
  mode = "light",
  renderBar,
  renderStat,
  sectionProps,
  stats = [],
  theme = "white"
}: GraphicStatsBlockViewProps<TStat, TBar>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const link = takeFirst(linkValue);
  const icon = getPlainLinkIcon(link);
  const renderCTABlock = isLinkValid(link) || Boolean(callToActionText);
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.background, themeStyle.border]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledDiv2 $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.border, themeStyle.background]}>
            <_StyledDiv3 $_css3={[{
            "@media (min-width: 1024px)": {
              "display": "flex",
              "borderBottomWidth": "1px"
            }
          }, themeStyle.border]}>
              <_StyledDiv4 $_css4={[{
              "@media (min-width: 1024px)": {
                "display": "flex",
                "width": "33.333333%",
                "flexDirection": "column",
                "justifyContent": "space-between",
                "borderRightWidth": "1px"
              }
            }, themeStyle.border]}>
                <_StyledDiv5 $_css5={[{
                "borderBottomWidth": "1px",
                "padding": "4rem"
              }, themeStyle.border]}>
                  {headline && <_StyledP>{headline}</_StyledP>}
                  {caption && <_StyledP2>{parseTextWithHighlights(caption)}</_StyledP2>}
                </_StyledDiv5>

                {hasItems(graphic) && <_StyledDiv6>
                    <GraphicStatsGraphicView bars={graphic} getBarKey={getBarKey} renderBar={renderBar} />
                  </_StyledDiv6>}

                {hasItems(stats) && <_StyledUl $_css6={[{
                "borderBottomWidth": "1px",
                "@media (min-width: 1024px)": {
                  "borderBottomWidth": "0px"
                }
              }, themeStyle.border]}>
                    {stats.map((stat, i) => <React.Fragment key={getStatKey?.(stat, i) || i}>{renderStat(stat, i)}</React.Fragment>)}
                  </_StyledUl>}
              </_StyledDiv4>

              {hasItems(graphic) && <_StyledDiv7 $_css7={[{
              "position": "relative",
              "display": "none",
              "width": "66.666667%",
              "alignItems": "center",
              "justifyContent": "center",
              "padding": "4rem",
              "@media (min-width: 1024px)": {
                "display": "flex"
              }
            }, themeStyle.border]}>
                  <GraphicStatsGraphicView bars={graphic} getBarKey={getBarKey} renderBar={renderBar} />
                </_StyledDiv7>}
            </_StyledDiv3>

            {renderCTABlock && <_StyledDiv8 $_css8={[{
            "paddingLeft": "1.6rem",
            "paddingRight": "1.6rem",
            "@media (min-width: 1024px)": {
              "paddingLeft": "0",
              "paddingRight": "0"
            }
          }, themeStyle.border]}>
                <_StyledDiv9 $_css9={[{
              "borderLeftWidth": "1px",
              "borderRightWidth": "1px",
              "padding": "2.4rem",
              "@media (min-width: 1024px)": {
                "display": "flex",
                "justifyContent": "space-between",
                "borderLeftWidth": "0px",
                "borderRightWidth": "0px",
                "padding": "4rem"
              }
            }, themeStyle.border]}>
                  {callToActionText && <_StyledP3>{callToActionText}</_StyledP3>}
                  {isLinkValid(link) && <_StyledDiv10>
                      <Button as={Link} {...link} icon={icon} variant="underline" />
                    </_StyledDiv10>}
                </_StyledDiv9>
              </_StyledDiv8>}
          </_StyledDiv2>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-dielgb-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-dielgb-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-dielgb-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-dielgb-3"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-dielgb-4"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-dielgb-5"
})`${p => p.$_css5}`;
var _StyledP = _styled7("p").withConfig({
  componentId: "sc-sf-dielgb-6"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledP2 = _styled8("p").withConfig({
  componentId: "sc-sf-dielgb-7"
})({
  "marginTop": "2.4rem",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400"
});
var _StyledDiv6 = _styled9("div").withConfig({
  componentId: "sc-sf-dielgb-8"
})({
  "padding": "2.4rem",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledUl = _styled10("ul").withConfig({
  componentId: "sc-sf-dielgb-9"
})`${p => p.$_css6}`;
var _StyledDiv7 = _styled11("div").withConfig({
  componentId: "sc-sf-dielgb-10"
})`${p => p.$_css7}`;
var _StyledDiv8 = _styled12("div").withConfig({
  componentId: "sc-sf-dielgb-11"
})`${p => p.$_css8}`;
var _StyledDiv9 = _styled13("div").withConfig({
  componentId: "sc-sf-dielgb-12"
})`${p => p.$_css9}`;
var _StyledP3 = _styled14("p").withConfig({
  componentId: "sc-sf-dielgb-13"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledDiv10 = /*#__PURE__*/_styled15("div").withConfig({
  displayName: "graphic-stats___StyledDiv10",
  componentId: "sc-za6qye-0"
})({
  "marginTop": "2.4rem",
  "@media (min-width: 1024px)": {
    "marginTop": "0"
  }
});